import { type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { shouldRetryCanvasRequest } from '~/swr-hooks/canvases/canvasRetryUtils';
import { type CanvasDetail } from '~/swr-hooks/canvases/types';

export const CANVAS_DETAIL = 'CANVAS_DETAIL';

export type CanvasDetailKey = [typeof CANVAS_DETAIL, UseCanvasParams];

export const getCanvasDetailKey = (params: UseCanvasParams) => {
  return [CANVAS_DETAIL, params];
};

export const isCanvasDetailKey = (key: QueryKey): key is CanvasDetailKey =>
  Array.isArray(key) && key[0] === CANVAS_DETAIL && isObject(key[1]);

export type UseCanvasParams = Omit<NonNullable<Parameters<typeof Canvases.get>[0]>, 'canvasId' | 'workspaceId'> & {
  canvasId: string | undefined;
  workspaceId: string | undefined;
};

export type UseCanvasOptions = {
  enabled?: boolean;
};

export const useCanvas = (params: UseCanvasParams, options: UseCanvasOptions = {}) => {
  const workspaceId = params.workspaceId;
  const canvasId = params.canvasId;

  const query = useQuery({
    queryKey: getCanvasDetailKey(params),
    queryFn:
      workspaceId && canvasId
        ? () =>
            callAirApi<CanvasDetail>(() =>
              Canvases.get({
                ...params,
                workspaceId,
                canvasId,
                includeInactiveNodes: true,
              }),
            )
        : skipToken,
    retry: shouldRetryCanvasRequest,
    enabled: !!workspaceId && !!canvasId && (options.enabled ?? true),
  });

  return { ...query, canvas: query.data };
};
